import * as React from "react";

import { useAuth } from "@/providers";
import type { ViewRecord } from "@/shared/domain/views";
import {
  useCreateView,
  useDeleteView,
  useDuplicateView,
  useUpdateView,
} from "@/shared/domain/views/hooks";

export interface ManageViews {
  /** Change a View's name. Its definition is untouched. */
  renameView: (view: ViewRecord, name: string) => Promise<ViewRecord>;
  /** Copy a View, authored by the caller and private regardless of the source. */
  duplicateView: (view: ViewRecord) => Promise<ViewRecord>;
  /** Copy a View into the org tier, authored by the caller. Source untouched. */
  createTeamCopy: (view: ViewRecord) => Promise<ViewRecord>;
  /** Delete a View permanently. */
  deleteView: (view: ViewRecord) => Promise<void>;
  /** Whether the caller authored this View — the tier that may rename or delete it. */
  isAuthor: (view: ViewRecord) => boolean;
  /** True while any of the four is in flight. */
  isBusy: boolean;
}

/**
 * Rename, copy and delete, for the picker's per-row menu.
 *
 * Bundled because the row menu needs all of them plus `isAuthor`, and because
 * the rule is worth stating once: renaming and deleting are author-only
 * (ENG-2881), so the menu hides both rather than offering controls the API will
 * refuse. Copying is offered to everyone — it reads the source and writes
 * something new, so it needs only `can_view`.
 */
export function useManageViews(): ManageViews {
  const { currentUser } = useAuth();
  const update = useUpdateView();
  const duplicate = useDuplicateView();
  const create = useCreateView();
  const remove = useDeleteView();

  const updateAsync = update.mutateAsync;
  const duplicateAsync = duplicate.mutateAsync;
  const createAsync = create.mutateAsync;
  const removeAsync = remove.mutateAsync;

  const renameView = React.useCallback(
    (view: ViewRecord, name: string) =>
      updateAsync({ viewId: view.view_id, updates: { name } }),
    [updateAsync],
  );

  const duplicateView = React.useCallback(
    (view: ViewRecord) =>
      // No body: the server copies the definition whole. Re-posting a parsed
      // one would drop any field of the versioned shape this build predates.
      duplicateAsync({ viewId: view.view_id }),
    [duplicateAsync],
  );

  const createTeamCopy = React.useCallback(
    (view: ViewRecord) =>
      // A create rather than a duplicate, because only `create` accepts a
      // visibility — `duplicate` forces the copy private. The definition is
      // handed back exactly as it arrived: `HttpResponse.json` is a parse plus
      // a cast, so it still carries any field a newer backend added, and
      // passing the object through preserves them. Rebuilding it field by
      // field here would not.
      createAsync({
        name: `${view.name} (copy)`,
        target: view.target,
        definition: view.definition,
        visibility: "organization",
      }),
    [createAsync],
  );

  const deleteView = React.useCallback(
    (view: ViewRecord) => removeAsync(view.view_id),
    [removeAsync],
  );

  const isAuthor = React.useCallback(
    (view: ViewRecord) =>
      currentUser?.user_id !== undefined &&
      view.created_by === currentUser.user_id,
    [currentUser?.user_id],
  );

  return {
    renameView,
    duplicateView,
    createTeamCopy,
    deleteView,
    isAuthor,
    isBusy:
      update.isPending ||
      duplicate.isPending ||
      create.isPending ||
      remove.isPending,
  };
}
